import type { HTMLAttributes, ReactNode } from 'react';
import { useI18nT } from '../../i18n/index.js';

export interface EmptyProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'title'> {
  /** Optional illustration / icon above the title. */
  icon?: ReactNode;
  /** Short heading — defaults to the current locale's generic "empty" label. */
  title?: ReactNode;
  /** Optional longer body text that explains why the list is empty. */
  description?: ReactNode;
  /** Call-to-action slot (usually a <Button>). */
  action?: ReactNode;
  /** Layout density — `compact` is used inside Select panels, CommandPalette
   *  results, NotificationCenter tabs. `standard` is the default. */
  size?: 'compact' | 'standard';
  className?: string;
}

/** Canonical empty-state block. Drop-in for "no data yet" sections — list
 *  pages, search results, filter outputs. Spec 14 §5. */
export function Empty(props: EmptyProps): ReactNode {
  const { icon, title, description, action, size = 'standard', className, ...rest } = props;
  const t = useI18nT();
  const classes = ['vq-empty', className].filter(Boolean).join(' ');
  const resolvedTitle = title ?? t('common.empty');
  return (
    <div
      {...rest}
      className={classes}
      data-component="Empty"
      data-size={size}
      role="status"
      aria-live="polite"
    >
      {icon !== undefined ? (
        <span className="vq-empty__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="vq-empty__title">{resolvedTitle}</div>
      {description !== undefined ? <div className="vq-empty__desc">{description}</div> : null}
      {action !== undefined ? <div className="vq-empty__action">{action}</div> : null}
    </div>
  );
}
